<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="PatientVerificationList" label="Patient Verifier" />
      <breadcrumb-link view="PatientVerificationCreate" label="New" :active="true" />
    </breadcrumb-list>
    <section>
      <div class="container is-max-hd">
        <div class="columns is-flex-wrap-wrap">
          <div class="column is-two-thirds-desktop form-column">
            <div class="box">
              <div class="header-container">
                <h1 class="title">New Patient Verification</h1>
              </div>
              <hr />
              <b-message v-if="message" type="is-danger" :closable="false">{{ message }}</b-message>
              <b-message v-if="loaded && !canSubmit" type="is-warning" :closable="false">
                {{
                  subscription && subscription.is_active
                    ? 'Your allowance has been reached. You can still view saved results.'
                    : 'An active or trialing subscription is required.'
                }}
                <router-link v-if="!hasActiveSubscription && isManager" :to="{ name: 'PlanList' }">
                  View subscription plans
                </router-link>
                <span v-else-if="!hasActiveSubscription">
                  Contact your organization manager to subscribe.
                </span>
              </b-message>
              <form @submit.prevent="submit">
                <fieldset :disabled="submitting || uncertain">
                  <h2 class="section-title has-background-primary-light has-text-primary-dark">
                    Patient
                  </h2>
                  <div class="columns is-multiline">
                    <div v-for="field in nameFields" :key="field.key" class="column is-half">
                      <b-field
                        :message="errors[field.key]"
                        :type="errors[field.key] ? 'is-danger' : ''"
                      >
                        <template #label>
                          {{ field.label }}
                          <span v-if="field.key === 'LastName'" class="has-text-danger">*</span>
                        </template>
                        <b-input
                          v-model="form[field.key]"
                          :required="field.key === 'LastName'"
                          maxlength="250"
                          :has-counter="false"
                        />
                      </b-field>
                    </div>
                    <div class="column is-half">
                      <b-field
                        label="Date of birth"
                        :message="errors.DOB"
                        :type="errors.DOB ? 'is-danger' : ''"
                      >
                        <b-datepicker
                          v-model="form.DOB"
                          :min-date="minDate"
                          :max-date="maxDate"
                          :date-parser="dateParser"
                          editable
                          locale="en-US"
                          placeholder="Click to select..."
                          icon="calendar-today"
                          :mobile-modal="false"
                          :mobile-native="false"
                          trap-focus
                          :use-html5-validation="false"
                        />
                      </b-field>
                    </div>
                    <div class="column is-full">
                      <b-field
                        label="SSN"
                        :message="errors.SSN"
                        :type="errors.SSN ? 'is-danger' : ''"
                      >
                        <b-input
                          v-model="form.SSN"
                          placeholder="Last four digits or full SSN."
                          type="text"
                          autocomplete="off"
                          maxlength="11"
                          :has-counter="false"
                        />
                      </b-field>
                    </div>
                  </div>
                  <h2 class="section-title has-background-primary-light has-text-primary-dark mt-4">
                    Address
                  </h2>
                  <div class="columns is-multiline">
                    <div
                      v-for="field in addressFields"
                      :key="field.key"
                      class="column is-half"
                    >
                      <b-field
                        :label="field.label"
                        :message="errors[field.key]"
                        :type="errors[field.key] ? 'is-danger' : ''"
                      >
                        <b-input
                          v-model="form.Address[field.key]"
                          :placeholder="field.placeholder"
                          maxlength="250"
                          :has-counter="false"
                        />
                      </b-field>
                    </div>
                    <div class="column is-half">
                      <b-field
                        label="ZIP code"
                        :message="errors.ZipCode"
                        :type="errors.ZipCode ? 'is-danger' : ''"
                      >
                        <b-input
                          v-model="form.Address.ZipCode"
                          maxlength="250"
                          :has-counter="false"
                        />
                      </b-field>
                    </div>
                    <div class="column is-full">
                      <AjaxSelectField
                        label="Select the Patient's state of residence"
                        placeholder="e.g. California"
                        :options="states"
                        :errors="errors.State"
                        :value="form.Address.State"
                        :disabled="submitting || uncertain"
                        @input="form.Address.State = $event || ''"
                      />
                    </div>
                  </div>
                </fieldset>
                <div class="submit-button-container buttons">
                  <b-button
                    type="is-primary"
                    native-type="submit"
                    size="is-medium"
                    class="submit-button"
                    :loading="submitting"
                    :disabled="submitting || (!uncertain && !canSubmit)"
                  >
                    {{ uncertain ? 'Retrieve Submitted Verification' : 'Verify Patient Information' }}
                  </b-button>
                </div>
              </form>
            </div>
          </div>
          <div class="column instructions-column">
            <div class="box instructions-box">
              <div class="instructions-header" @click="toggleInstructions">
                <h2 class="title is-5 instructions-title">
                  <b-icon icon="information-outline" size="is-small" class="mr-2" />
                  Instructions
                </h2>
                <b-icon
                  :icon="isInstructionsExpanded ? 'chevron-up' : 'chevron-down'"
                  class="toggle-icon"
                />
              </div>
              <div
                class="instructions-content"
                :class="{ 'is-collapsed': !isInstructionsExpanded }"
              >
                <hr />
                <div class="content">
                  <p>Last name is required. All other fields are optional.</p>
                  <b-notification type="is-info is-light" :closable="false">
                    <strong>More details help narrow the results and improve match accuracy.</strong>
                    Add the patient's first name, date of birth, SSN, or home address when available.
                    A last-name-only search may return many patients.
                  </b-notification>
                  <ul>
                    <li>Select the patient's date of birth from the calendar.</li>
                    <li>For SSN, enter the last four digits or the full nine-digit number.</li>
                    <li>Use the patient's home address, including city, state, and ZIP if known.</li>
                    <li>Review the returned details to confirm the correct patient.</li>
                  </ul>
                  <p class="is-size-7 has-text-grey">
                    Each submitted search uses one verification, including searches with no matches.
                    Opening a saved result does not use another verification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import { dateParser } from '../utils/dateParser';
import request from '../request';
import { format } from 'date-fns';
import { mapGetters } from 'vuex';
import AjaxSelectField from '../components/fields/AjaxSelectField.vue';
export default {
  name: 'PatientVerificationCreate',
  components: { AjaxSelectField },
  data() {
    return {
      subscription: null,
      loaded: false,
      states: [],
      submitting: false,
      uncertain: false,
      errors: {},
      message: '',
      submittedPayload: null,
      isInstructionsExpanded: window.innerWidth > 1024,
      isMobile: window.innerWidth <= 1024,
      nameFields: [
        { key: 'FirstName', label: 'First name' },
        { key: 'MiddleName', label: 'Middle name' },
        { key: 'LastName', label: 'Last name' },
      ],
      addressFields: [
        {
          key: 'AddressLine1',
          label: 'Street address',
          placeholder: 'Building number and street, e.g. 123 Main Street',
        },
        {
          key: 'AddressLine2',
          label: 'Address line 2',
          placeholder: 'Apartment, suite, or unit, e.g. Apt 4B',
        },
        { key: 'City', label: 'City' },
      ],
      form: {
        FirstName: '',
        MiddleName: '',
        LastName: '',
        DOB: null,
        SSN: '',
        Address: { AddressLine1: '', AddressLine2: '', City: '', State: '', ZipCode: '' },
      },
    };
  },
  computed: {
    ...mapGetters(['hasActiveSubscription', 'isManager']),
    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 100);
      return date;
    },
    maxDate() {
      return new Date();
    },
    verificationLimit() {
      return this.subscription?.user_plans?.Base?.Plan?.patient_verifications || 0;
    },
    canSubmit() {
      return Boolean(
        this.subscription?.is_active &&
          this.subscription.patient_verification_used_quota < this.verificationLimit
      );
    },
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    try {
      const [subscription, states] = await Promise.all([
        this.hasActiveSubscription
          ? request.get('/subscriptions/current').catch((err) => {
              if ([403, 404].includes(err.response?.status))
                return { data: { subscription: null } };
              throw err;
            })
          : Promise.resolve({ data: { subscription: null } }),
        request.get('/patient-verifications/states'),
      ]);
      this.subscription = subscription.data.subscription;
      this.states = states.data.map((state) => ({ id: state.abbreviation, name: state.name }));
    } catch (err) {
      this.message = 'Unable to load the verification form.';
    } finally {
      this.loaded = true;
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    dateParser: dateParser,
    toggleInstructions() {
      if (this.isMobile) {
        this.isInstructionsExpanded = !this.isInstructionsExpanded;
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 1024;
      if (!this.isMobile) {
        this.isInstructionsExpanded = true;
      }
    },
    async submit() {
      if (this.submitting) return;
      this.errors = {};
      this.message = '';
      if (!this.uncertain) {
        const f = this.form;
        const ssn = f.SSN.replace(/[-\s]/g, '');
        if (ssn && !/^(\d{4}|\d{9})$/.test(ssn)) this.errors.SSN = 'Enter four or nine digits.';
        if (!f.LastName.trim()) this.errors.LastName = 'Last name is required.';
        if (this.message || Object.keys(this.errors).length) return;
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        const id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        this.submittedPayload = {
          ...JSON.parse(JSON.stringify(f)),
          DOB: f.DOB ? format(f.DOB, 'MM/dd/yyyy') : '',
          client_request_id: id,
        };
      }
      this.submitting = true;
      try {
        const resp = await request.post('/patient-verifications', this.submittedPayload);
        if (!resp?.data?.patient_verification?.id)
          throw new Error('No submission response received');
        this.form.SSN = '';
        this.submittedPayload = null;
        this.uncertain = false;
        this.$router.push({
          name: 'PatientVerificationDetail',
          params: { id: resp.data.patient_verification.id },
        });
      } catch (err) {
        this.errors = err.response?.data?.errors || {};
        this.uncertain = !err.response || err.response.status >= 500;
        this.message =
          this.errors.NOFIELD ||
          (this.uncertain
            ? 'The submission may have been received. Retrieve the same submission or check history before starting another search.'
            : 'Unable to submit verification.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.box {
  padding: 2rem;
}

.header-container {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.header-container .title {
  margin-bottom: 0;
  flex-shrink: 0;
}

.form-column >>> .input::placeholder {
  font-size: 0.75rem;
}

.section-title {
  border-left: 3px solid #7957d5;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.submit-button-container {
  margin-top: 1.5rem;
  width: 100%;
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.instructions-box {
  background-color: #fff;
  border: 1px solid #e9ecef;
}

.instructions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.instructions-title {
  display: flex;
  align-items: center;
  color: #495057;
  margin-bottom: 0 !important;
}

.toggle-icon {
  display: none;
  color: #6c757d;
  transition: transform 0.2s ease;
}

.instructions-content {
  overflow: hidden;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  max-height: 1000px;
  opacity: 1;
}

.instructions-content hr {
  background-color: #dee2e6;
  height: 1px;
  margin: 1rem 0;
}

@media (max-width: 1024px) {
  .columns.is-flex-wrap-wrap {
    display: flex;
    flex-direction: column;
  }
  .instructions-column {
    order: -1;
    margin-top: 1rem;
    margin-bottom: 0;
  }
  .form-column {
    order: 1;
  }

  .instructions-header {
    cursor: pointer;
  }

  .toggle-icon {
    display: block;
  }

  .instructions-content.is-collapsed {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
  }

  .submit-button-container .button {
    width: 100%;
    margin-right: 0;
  }
}
</style>
